import { create } from '@/shared/utils/zustandVue'

import { t } from '../../i18n/i18n'
import router from '../../router'
import { ErrorService } from '../service/errorService'
import { SessionService } from '../service/session.service'
import { type Version, VersionService } from '../service/version.service'
import { SnackbarService } from '../snackbar/snackbar.service'

/** The redirect target survives the reload done on session expiry
 * (axios.interceptors.ts). */
const REDIRECT_STORAGE_KEY = 'redirectAfterLogin'

function persistRedirect(path: string | null): void {
  try {
    if (path == null) sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
    else sessionStorage.setItem(REDIRECT_STORAGE_KEY, path)
  } catch {
    // storage unavailable (private mode…): in-memory redirect only
  }
}

function readPersistedRedirect(): string | null {
  try {
    return sessionStorage.getItem(REDIRECT_STORAGE_KEY)
  } catch {
    return null
  }
}

class SessionState {
  loading = true
  key: string | null = null
  principal: null | PrincipalDto = null
  redirectAfterLogin: string | null = null
  version: Version | null = null
}

export type PrincipalDto = {
  email: string
  name: string
  roles: string[]
}

type SessionStore = SessionState & {
  setRedirectAfterLogin: (path?: string) => void
  resetLoginOn401: () => void
  loadSession: () => Promise<void>
  login: (credentials: { username: string; password: string }) => Promise<void>
  logout: () => Promise<void>
  checkVersion: () => Promise<void>
}

/**
 * Session store — idiomatic zustand-vue.
 *
 * Holds the authenticated principal, API key, redirect target, and app
 * version. Actions handle login/logout/session-load/version-check; mutations
 * are now plain actions (`setRedirectAfterLogin`, `resetLoginOn401`).
 */
export const useSessionStore = create<SessionStore>((set, get) => ({
  ...new SessionState(),
  setRedirectAfterLogin: (path?: string) => {
    const redirectAfterLogin = path != null && path !== '/login' ? path : '/'
    set({ redirectAfterLogin })
    persistRedirect(redirectAfterLogin)
  },
  resetLoginOn401: () => {
    set({ principal: null, key: null })
  },
  loadSession: async () => {
    try {
      set({ loading: true })
      const { principal, key } = await SessionService.getSession()
      set({ principal, key })
    } catch (e) {
      ErrorService.handleError(e, false)
    } finally {
      set({ loading: false })
    }
  },
  login: async ({ username, password }) => {
    try {
      const { principal, key } = await SessionService.login(username, password)
      set({ principal, key })
      const redirect = get().redirectAfterLogin ?? readPersistedRedirect()
      set({ redirectAfterLogin: null })
      persistRedirect(null)
      await router.replace(redirect ?? '/')
    } catch {
      SnackbarService.error(t('LOGIN.ERROR'))
    }
  },
  logout: async () => {
    try {
      set({ loading: true })
      await SessionService.logout()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ principal: null, key: null, loading: false })
      await router.replace('/login').catch(() => {
        /* do nothing avoid throwing in finally */
      })
    }
  },
  checkVersion: async () => {
    try {
      const version = await VersionService.getVersion()
      set({ version })
    } catch (e) {
      ErrorService.handleError(e, false)
    }
  },
}))
